/**
 * Canonical blockchain network registry.
 *
 * Single source of truth for network keys, display labels and token-standard
 * badges used across deposit, withdrawal, transaction history and admin views.
 * A mirrored copy lives in `supabase/functions/_shared/networks.ts`.
 */

export interface NetworkMeta {
  /** Canonical lowercase key stored in the database and sent to providers */
  key: string;
  /** Human readable label shown in the UI (includes token standard when relevant) */
  label: string;
  /** Short chain name without the token standard */
  chain: string;
  /** Token standard badge, when the chain carries one */
  standard?: string;
  /** Alternate spellings / legacy values that must normalize to `key` */
  aliases?: string[];
}

export const NETWORKS: NetworkMeta[] = [
  { key: "bitcoin", label: "Bitcoin", chain: "Bitcoin", aliases: ["btc", "bitcoin mainnet"] },
  { key: "ethereum", label: "Ethereum (ERC20)", chain: "Ethereum", standard: "ERC20", aliases: ["eth", "erc20", "erc-20", "ethereum mainnet", "eth mainnet"] },
  { key: "tron", label: "Tron (TRC20)", chain: "Tron", standard: "TRC20", aliases: ["trx", "trc20", "trc-20", "tron network"] },
  { key: "bsc", label: "BNB Smart Chain (BEP20)", chain: "BNB Smart Chain", standard: "BEP20", aliases: ["bep20", "bep-20", "bnb", "bnb smart chain", "binance smart chain", "binance-smart-chain", "bsc mainnet"] },
  { key: "polygon", label: "Polygon (POS)", chain: "Polygon", standard: "POS", aliases: ["matic", "pol", "polygon pos", "polygon mainnet"] },
  { key: "arbitrum", label: "Arbitrum One", chain: "Arbitrum One", aliases: ["arb", "arbitrum one", "arbitrum-one"] },
  { key: "optimism", label: "Optimism", chain: "Optimism", aliases: ["op", "op mainnet"] },
  { key: "base", label: "Base", chain: "Base", aliases: ["base mainnet"] },
  { key: "avalanche", label: "Avalanche C-Chain", chain: "Avalanche", aliases: ["avax", "avalanche c-chain", "avaxc", "c-chain"] },
  { key: "solana", label: "Solana (SPL)", chain: "Solana", standard: "SPL", aliases: ["sol", "spl"] },
  { key: "ton", label: "TON (TEP-74)", chain: "TON", standard: "TEP-74", aliases: ["toncoin", "the open network"] },
  { key: "ripple", label: "XRP Ledger", chain: "XRP Ledger", aliases: ["xrp", "xrpl", "xrp ledger"] },
  { key: "stellar", label: "Stellar", chain: "Stellar", aliases: ["xlm"] },
  { key: "litecoin", label: "Litecoin", chain: "Litecoin", aliases: ["ltc"] },
  { key: "dogecoin", label: "Dogecoin", chain: "Dogecoin", aliases: ["doge"] },
  { key: "bch", label: "Bitcoin Cash", chain: "Bitcoin Cash", aliases: ["bitcoin cash", "bitcoincash", "bchn"] },
  { key: "dash", label: "Dash", chain: "Dash" },
  { key: "zcash", label: "Zcash", chain: "Zcash", aliases: ["zec"] },
  { key: "cardano", label: "Cardano", chain: "Cardano", aliases: ["ada"] },
  { key: "polkadot", label: "Polkadot", chain: "Polkadot", aliases: ["dot"] },
  { key: "cosmos", label: "Cosmos", chain: "Cosmos", aliases: ["atom", "cosmos hub"] },
  { key: "algorand", label: "Algorand (ASA)", chain: "Algorand", standard: "ASA", aliases: ["algo", "asa"] },
  { key: "aptos", label: "Aptos", chain: "Aptos", aliases: ["apt"] },
  { key: "tezos", label: "Tezos", chain: "Tezos", aliases: ["xtz"] },
  { key: "near", label: "NEAR Protocol", chain: "NEAR", aliases: ["near protocol"] },
  { key: "celo", label: "Celo", chain: "Celo" },
  { key: "kaia", label: "Kaia", chain: "Kaia", aliases: ["klaytn", "klay"] },
  { key: "scroll", label: "Scroll", chain: "Scroll" },
  { key: "kava", label: "Kava EVM", chain: "Kava", aliases: ["kavaevm", "kava evm"] },
  { key: "monero", label: "Monero", chain: "Monero", aliases: ["xmr"] },
  { key: "dogechain", label: "Dogechain", chain: "Dogechain" },
];

const BY_KEY = new Map<string, NetworkMeta>();
const ALIAS_TO_KEY = new Map<string, string>();

for (const n of NETWORKS) {
  BY_KEY.set(n.key, n);
  ALIAS_TO_KEY.set(n.key, n.key);
  ALIAS_TO_KEY.set(n.label.toLowerCase(), n.key);
  ALIAS_TO_KEY.set(n.chain.toLowerCase(), n.key);
  for (const a of n.aliases ?? []) ALIAS_TO_KEY.set(a.toLowerCase(), n.key);
}

/** Legacy label map kept for backwards compatibility (chain names only). */
export const NETWORK_LABELS: Record<string, string> = Object.fromEntries(
  NETWORKS.map((n) => [n.key, n.label]),
);

/** Canonical network keys, alphabetical by label — used by admin selects. */
export const CANONICAL_NETWORKS = [...NETWORKS].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Normalize any network value (legacy token standards, tickers, spacing,
 * casing) to its canonical lowercase key. Unknown values are returned
 * lowercased and trimmed so lookups stay deterministic.
 */
export function normalizeNetwork(network: string | null | undefined): string {
  if (!network) return "";
  const raw = String(network).trim().toLowerCase().replace(/[\s_]+/g, " ");
  return ALIAS_TO_KEY.get(raw) ?? ALIAS_TO_KEY.get(raw.replace(/[\s-]/g, "")) ?? raw;
}

/** True when the value maps onto a known canonical network. */
export function isKnownNetwork(network: string | null | undefined): boolean {
  return BY_KEY.has(normalizeNetwork(network));
}

export function getNetworkMeta(network: string | null | undefined): NetworkMeta | undefined {
  return BY_KEY.get(normalizeNetwork(network));
}

/** Full display label, e.g. "Ethereum (ERC20)". */
export function getNetworkLabel(network: string | null | undefined): string {
  if (!network) return "—";
  return getNetworkMeta(network)?.label ?? network;
}

/** Chain name without the token standard, e.g. "Ethereum". */
export function getNetworkChain(network: string | null | undefined): string {
  if (!network) return "—";
  return getNetworkMeta(network)?.chain ?? network;
}

/** Token standard badge, e.g. "ERC20". Empty when the chain has none. */
export function getNetworkStandard(network: string | null | undefined): string | null {
  return getNetworkMeta(network)?.standard ?? null;
}
